/**
 * Accesos → Ámbitos → one ámbito (Plan J, J3 — mockup 3, detail half).
 *
 * THE SENTENCE THIS SCREEN EXISTS TO SAY
 * --------------------------------------
 * **Saving the rules closes the scope until it is rebuilt.** The save
 * retires the scope's files and pointers on every project and deletes
 * its AI snapshots, under the tenant write lock, in the same request
 * (the runbook's §8). From that instant every reader bound to this id —
 * including people in ONE group, who keep this exact id — gets a 503 on
 * every report, never the previous cut, until somebody rebuilds (here,
 * or at the project's next reload, which on HP can be a full day away).
 * If a reload holds the lock, the save is refused with a 409 and nothing
 * is written. The PUT's own response says all of this with what actually
 * happened, and this screen shows that sentence under a WARNING title
 * with the warning glyph — the state is carried by shape and words, not
 * by colour alone.
 *
 * So the save is impact-acknowledged: the dry run
 * (`POST /{id}/impact`) is fetched BEFORE the PUT, its
 * `affected_group_ids` is echoed back in `acknowledge_affects`, and a
 * caller who has not read the consequence cannot name it. That is the
 * whole mechanism, and it is the backend's, not a courtesy of this
 * screen.
 *
 * WHY THE STALENESS VERDICTS ARE NOT COMPUTED HERE
 * ------------------------------------------------
 * `state` and `stuck` come from the server and are rendered, never
 * re-derived. The runbook records that two of the three staleness
 * comparisons are timezone-fragile — `scope_builds` timestamps are
 * naive-UTC while `projects.data_source_loaded_at` is TIMESTAMPTZ — and
 * that `coalesce(built_at, started_at)` is required because `built_at`
 * is written only for `built`. A client re-derivation would reproduce
 * exactly the two silent failures that correction fixed.
 *
 * THE REBUILD IS SYNCHRONOUS AND MAY OUTLIVE THE PROXY
 * ---------------------------------------------------
 * It holds the TENANT WRITE LOCK for the length of the build and every
 * other writer of this client waits behind it (J1 measured 68–166 s for
 * a two-scope round on a 1166 MiB file). On an HP-sized project a proxy
 * times out first — and when it does, this request is not the source of
 * truth: the builds list is. So a failed request refreshes the builds
 * rather than declaring failure, `skipped_concurrent` is its own state
 * and not an error, and a `building` row past the threshold shows the
 * backend's own `stuck_hint`: the literal CLI line that clears it. A
 * sentence, not a button, because clearing a row a live worker still
 * holds would let a second build write the same files.
 */

import { Button } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  DateCell,
  PageContainer,
  PageHeader,
  Section,
  SectionStack,
  StatusChip,
  type Column,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { ArrowLeft, Hammer, RefreshCw, Save } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useT } from "@/i18n";
import {
  scopesApi,
  type DimensionKeyOut,
  type ScopeBuildOut,
  type ScopeDetailOut,
  type ScopeImpactOut,
  type ScopeProjectStatusOut,
  type ScopeRule,
} from "@/lib/access-api";
import { DictChip } from "@/pages/admin-access/AccessCells";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import {
  buildStatusDict,
  scopeStateDict,
} from "@/pages/admin-access/access-status";
import {
  ScopeRulesEditor,
  type ReferenceProjects,
} from "@/pages/admin-access/ScopeRulesEditor";
import { reloadThenReport } from "@/pages/admin-access/reload-after-failure";

/** Two rule lists mean the same thing when their normalised JSON does.
 *  Structural, not reference: the editor rebuilds the array on every
 *  keystroke, so `!==` would leave the Save button permanently live. */
export function rulesChanged(
  a: readonly ScopeRule[],
  b: readonly ScopeRule[],
): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

/** A rebuild cuts from the SAVED rules and its refresh() overwrites the
 *  local ones, so with unsaved edits it is off — save (or discard) first. */
export function rebuildDisabled(s: {
  rebuilding: string | null;
  editable: boolean | undefined;
  dirty: boolean;
}): boolean {
  return s.rebuilding !== null || s.editable !== true || s.dirty;
}

export function ScopeDetailPage(): JSX.Element {
  const { scopeId = "" } = useParams<{ scopeId: string }>();
  const { t, locale } = useT();

  const [scope, setScope] = useState<ScopeDetailOut | null>(null);
  const [catalogue, setCatalogue] = useState<DimensionKeyOut[]>([]);
  const [rules, setRules] = useState<ScopeRule[]>([]);
  const [references, setReferences] = useState<ReferenceProjects>({});
  const [impact, setImpact] = useState<ScopeImpactOut | null>(null);
  const [builds, setBuilds] = useState<ScopeBuildOut[]>([]);
  const [buildsTotal, setBuildsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** A save's notice is a WARNING with its own title — readers are on a
   *  503 until the rebuild — while a rebuild's notice is informational.
   *  The glyph and the title word carry that difference, never the
   *  colour alone. */
  const [noticeTone, setNoticeTone] = useState<"info" | "warning">("info");
  const [error, setError] = useState<string | null>(null);

  const states = useMemo(() => scopeStateDict(t), [t]);
  const buildStatuses = useMemo(() => buildStatusDict(t), [t]);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [detail, keys, buildList] = await Promise.all([
        scopesApi.get(scopeId),
        scopesApi.dimensionKeys(),
        scopesApi.builds(scopeId, { limit: 25 }),
      ]);
      setScope(detail);
      setRules(detail.rules);
      setCatalogue(keys.keys);
      setBuilds(buildList.builds);
      setBuildsTotal(buildList.total);
      setImpact(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [scopeId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const dirty = scope !== null && rulesChanged(rules, scope.rules);

  /** The dry run. Nothing is written; its `affected_group_ids` is the
   *  token the PUT demands back. */
  const preview = async (): Promise<void> => {
    setError(null);
    try {
      setImpact(await scopesApi.impact(scopeId, rules, { withEstimate: true }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const save = async (): Promise<void> => {
    if (scope === null || impact === null) return;
    setSaving(true);
    setError(null);
    try {
      const out = await scopesApi.update(scopeId, {
        rules,
        expected_updated_at: scope.updated_at,
        acknowledge_affects: impact.affected_group_ids,
      });
      // The backend's own sentence about what actually happened — what
      // was retired, how many snapshots went, who was rebound — stated
      // with the save rather than after it. Shown as a warning: from
      // this instant nobody reads through this scope until a rebuild.
      setNotice(out.effective);
      setNoticeTone("warning");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const rebuild = async (projectSlug: string): Promise<void> => {
    setRebuilding(projectSlug);
    setError(null);
    setNotice(null);
    let failure: string | null = null;
    try {
      const out = await scopesApi.rebuild(scopeId, projectSlug);
      setNotice(out.effective);
      setNoticeTone("info");
    } catch (err) {
      // A timeout here is NOT a failed build: the worker keeps going and
      // the builds list is the record. So the message is shown AND the
      // lists are refreshed, rather than one or the other.
      failure = err instanceof Error ? err.message : String(err);
    } finally {
      setRebuilding(null);
      if (failure === null) {
        await refresh();
      } else {
        await reloadThenReport(failure, refresh, setError);
      }
    }
  };

  const projectColumns: Column<ScopeProjectStatusOut>[] = [
    {
      id: "project",
      header: t("access.scopes.detail.col.project"),
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">
            {row.project_name}
          </div>
          <div className="truncate font-mono text-[13px] text-neutral-500">
            {row.project_slug}
          </div>
        </div>
      ),
    },
    {
      id: "state",
      header: t("access.scopes.detail.col.state"),
      width: "w-44",
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          <DictChip
            value={row.state}
            dictionary={states}
            t={t}
            title={row.not_applicable_detail ?? row.error_detail}
          />
          {row.stuck && (
            <StatusChip
              label={t("access.scopes.detail.stuck")}
              tone="danger"
              title={row.stuck_hint ?? undefined}
            />
          )}
        </div>
      ),
    },
    {
      id: "rows",
      header: t("access.scopes.detail.col.rows"),
      align: "right",
      width: "w-36",
      cell: (row) => <RowCountCell row={row} t={t} />,
    },
    {
      id: "version",
      header: t("access.scopes.detail.col.version"),
      width: "w-56",
      cell: (row) =>
        row.data_version === null ? (
          <span
            className="text-[13px] text-neutral-500"
            title={t("access.scopes.detail.noVersionHint")}
          >
            {t("access.common.notComputed")}
          </span>
        ) : (
          <span
            className="block truncate font-mono text-[13px] text-neutral-600"
            title={row.data_version}
          >
            {row.data_version}
          </span>
        ),
    },
    {
      id: "built",
      header: t("access.scopes.detail.col.built"),
      width: "w-44",
      cell: (row) => (
        <DateCell
          value={row.built_at ?? row.started_at}
          locale={locale}
          mode="absoluteWithTime"
          invalidHint={t("access.common.badDate")}
        />
      ),
    },
    {
      id: "rebuild",
      header: t("access.scopes.detail.col.rebuild"),
      width: "w-36",
      cell: (row) => (
        <Button
          variant="secondary"
          size="xs"
          leftIcon={<Hammer size={12} />}
          disabled={rebuildDisabled({ rebuilding, editable: scope?.editable, dirty })}
          onClick={() => void rebuild(row.project_slug)}
        >
          {rebuilding === row.project_slug
            ? t("access.scopes.detail.rebuilding")
            : t("access.scopes.detail.rebuild")}
        </Button>
      ),
    },
  ];

  const buildColumns: Column<ScopeBuildOut>[] = [
    {
      id: "project",
      header: t("access.scopes.detail.col.project"),
      cell: (row) => (
        <span className="font-mono text-[13px] text-neutral-700">
          {row.project_slug}
        </span>
      ),
    },
    {
      id: "status",
      header: t("access.scopes.detail.col.state"),
      width: "w-44",
      cell: (row) => (
        <DictChip
          value={row.status}
          dictionary={buildStatuses}
          t={t}
          title={row.error_detail}
        />
      ),
    },
    {
      id: "rows",
      header: t("access.scopes.detail.col.rows"),
      align: "right",
      width: "w-32",
      cell: (row) =>
        row.row_count === null ? (
          <span className="text-[13px] text-neutral-500">
            {t("access.common.notComputed")}
          </span>
        ) : (
          <span className="text-neutral-900">{row.row_count}</span>
        ),
    },
    {
      id: "started",
      header: t("access.scopes.detail.col.started"),
      width: "w-44",
      cell: (row) => (
        <DateCell
          value={row.started_at}
          locale={locale}
          mode="absoluteWithTime"
          invalidHint={t("access.common.badDate")}
        />
      ),
    },
  ];

  return (
    <PageContainer wide>
      <PageHeader
        eyebrow={
          <Link
            to="/admin/access/scopes"
            className="inline-flex items-center gap-1 text-[13px] font-medium text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft size={13} />
            {t("access.scopes.title")}
          </Link>
        }
        title={scope?.name ?? scopeId}
        subtitle={scope?.description === "" ? undefined : scope?.description}
        actions={
          <Button
            ghost
            size="sm"
            leftIcon={<RefreshCw size={14} />}
            onClick={() => void refresh()}
          >
            {t("access.common.refresh")}
          </Button>
        }
      />
      <AccessTabs active="scopes" />

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}
      {notice !== null && (
        <Callout
          tone={noticeTone}
          title={
            noticeTone === "warning"
              ? t("access.scopes.detail.retiredTitle")
              : t("access.scopes.detail.noticeTitle")
          }
        >
          {noticeTone === "warning" && (
            <p className="mb-2 font-semibold">
              {t("access.scopes.detail.retired")}
            </p>
          )}
          <span className="whitespace-pre-wrap">{notice}</span>
        </Callout>
      )}

      {loading || scope === null ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
          <Spinner size={16} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </div>
      ) : (
        <SectionStack>
          <Section
            title={t("access.scopes.detail.identity")}
            description={t("access.scopes.detail.identityDesc")}
          >
            <dl className="grid gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-[13px] font-semibold text-neutral-600">
                  {t("access.scopes.form.id")}
                </dt>
                <dd className="font-mono text-sm text-neutral-900">
                  {scope.id}
                </dd>
              </div>
              <div>
                <dt className="text-[13px] font-semibold text-neutral-600">
                  {t("access.scopes.detail.kind")}
                </dt>
                <dd className="text-sm text-neutral-900">{scope.scope_kind}</dd>
              </div>
              <div>
                <dt className="text-[13px] font-semibold text-neutral-600">
                  {t("access.scopes.detail.usedBy")}
                </dt>
                <dd className="text-sm text-neutral-900">
                  {t("access.scopes.usedBy", {
                    n: scope.usage.groups.length,
                    people: scope.usage.member_user_count,
                  })}
                </dd>
              </div>
            </dl>
          </Section>

          <Section
            title={t("access.scopes.detail.rules")}
            description={t("access.scopes.detail.rulesDesc")}
            actions={
              scope.editable ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!dirty}
                    onClick={() => void preview()}
                  >
                    {t("access.scopes.detail.preview")}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Save size={14} />}
                    disabled={!dirty || impact === null || saving}
                    onClick={() => void save()}
                  >
                    {t("common.save")}
                  </Button>
                </div>
              ) : undefined
            }
          >
            {!scope.editable && scope.not_editable_reason !== null && (
              <Callout
                tone="info"
                title={t("access.scopes.detail.notEditableTitle")}
                className="mb-3"
              >
                {scope.not_editable_reason}
              </Callout>
            )}

            {scope.editable && (
              <ScopeRulesEditor
                catalogue={catalogue}
                rules={rules}
                references={references}
                /* Any edit invalidates the impact that was read for the
                   PREVIOUS rules. The backend would refuse a stale
                   acknowledgement with a 409 — clearing it here is what
                   makes the screen agree with that instead of offering
                   a save it knows will be refused. */
                onChange={(next) => {
                  setRules(next);
                  setImpact(null);
                }}
                onReferenceChange={setReferences}
              />
            )}

            {!scope.editable && (
              <div className="flex flex-wrap gap-1">
                {scope.rules.length === 0 ? (
                  <span className="text-[13px] text-neutral-500">
                    {t("access.scopes.rules.all")}
                  </span>
                ) : (
                  scope.rules.map((rule) => (
                    <StatusChip
                      key={rule.dimension_key}
                      tone="neutral"
                      label={t("access.scopes.rules.chip", {
                        key: rule.dimension_key,
                        n: rule.values.length,
                      })}
                      title={rule.values.map((v) => String(v)).join(", ")}
                    />
                  ))
                )}
              </div>
            )}

            {dirty && impact === null && (
              <Callout
                tone="warning"
                title={t("access.scopes.detail.mustPreviewTitle")}
                className="mt-3"
              >
                {t("access.scopes.detail.mustPreview")}
              </Callout>
            )}

            {impact !== null && (
              <ImpactPanel impact={impact} t={t} />
            )}
          </Section>

          <Section
            title={t("access.scopes.detail.projects")}
            description={t("access.scopes.detail.projectsDesc")}
            flush
          >
            <DataTable
              columns={projectColumns}
              rows={scope.applies_to}
              rowKey={(row) => row.project_slug}
              caption={t("access.scopes.detail.projects")}
              expandedRow={(row) =>
                row.error_detail === null &&
                row.not_applicable_detail === null &&
                row.stuck_hint === null ? null : (
                  <div className="space-y-2">
                    {row.not_applicable_kind !== null && (
                      <p className="text-[13px] text-neutral-600">
                        <span className="font-mono">
                          {row.not_applicable_kind}
                        </span>
                        {row.not_applicable_detail === null
                          ? null
                          : ` — ${row.not_applicable_detail}`}
                      </p>
                    )}
                    {row.error_kind !== null && (
                      <p className="text-[13px] text-neutral-600">
                        <span className="font-mono">{row.error_kind}</span>
                        {row.error_detail === null
                          ? null
                          : ` — ${row.error_detail}`}
                      </p>
                    )}
                    {row.stuck_hint !== null && (
                      <Callout
                        tone="danger"
                        title={t("access.scopes.detail.stuckTitle")}
                      >
                        <span className="whitespace-pre-wrap font-mono text-[13px]">
                          {row.stuck_hint}
                        </span>
                      </Callout>
                    )}
                  </div>
                )
              }
            />
          </Section>

          <Section
            title={t("access.scopes.detail.builds")}
            description={t("access.scopes.detail.buildsDesc", {
              total: buildsTotal,
            })}
            flush
          >
            <DataTable
              columns={buildColumns}
              rows={builds}
              rowKey={(row) => row.id}
              caption={t("access.scopes.detail.builds")}
              footnote={
                buildsTotal > builds.length
                  ? t("access.scopes.detail.buildsCapped", {
                      shown: builds.length,
                      total: buildsTotal,
                    })
                  : undefined
              }
            />
          </Section>
        </SectionStack>
      )}
    </PageContainer>
  );
}

/**
 * `row_count` versus `project_rows`.
 *
 * `null` is UNKNOWN and renders as such. `0` is a real and different
 * answer that only a `no_mapping` build produces — an empty file, an
 * empty dashboard — so the two must not share a rendering.
 */
export function RowCountCell({
  row,
  t,
}: {
  row: ScopeProjectStatusOut;
  t: (key: string, params?: Record<string, string | number>) => string;
}): JSX.Element {
  if (row.row_count === null) {
    return (
      <span
        className="text-[13px] text-neutral-500"
        title={t("access.scopes.detail.noRowsHint")}
      >
        {t("access.common.notComputed")}
      </span>
    );
  }
  if (row.project_rows === null) {
    return <span className="text-neutral-900">{row.row_count}</span>;
  }
  return (
    <span className="text-neutral-900">
      {t("access.scopes.detail.rowsOf", {
        rows: row.row_count,
        total: row.project_rows,
      })}
    </span>
  );
}

/**
 * The dry run, on screen.
 *
 * It does NOT say whether the edit widens or narrows, and that is
 * deliberate on the backend's part: *"does this new expression keep more
 * rows?" is not answerable for an arbitrary expression and a partial
 * check would be read as a guarantee*. The row counts are the honest
 * surrogate and they are what this panel shows.
 *
 * A project the new rules no longer apply to says so, by its kind,
 * instead of «not computed»: it loses the scope's access entirely.
 */
export function ImpactPanel({
  impact,
  t,
}: {
  impact: ScopeImpactOut;
  t: (key: string, params?: Record<string, string | number>) => string;
}): JSX.Element {
  return (
    <div className="mt-3 space-y-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <p className="text-sm font-semibold text-neutral-800">
        {t("access.scopes.detail.impactTitle")}
      </p>
      {!impact.rules_valid && impact.rules_error !== null && (
        <Callout tone="danger" title={t("access.scopes.detail.rulesInvalid")}>
          <span className="whitespace-pre-wrap">{impact.rules_error}</span>
        </Callout>
      )}
      <p className="text-[13px] text-neutral-700">
        {t("access.scopes.detail.impactWho", {
          groups: impact.affected_group_ids.length,
          people: impact.affected_users.length,
        })}
      </p>
      <ul className="space-y-1">
        {impact.projects.map((p) => (
          <li key={p.project_slug} className="text-[13px] text-neutral-700">
            <span className="font-mono">{p.project_slug}</span>
            {" — "}
            {impact.rules_valid && !p.applicable_after ? (
              <>
                {t("access.scopes.state.notApplicable")}
                {p.not_applicable_kind_after !== null && (
                  <>
                    {" "}
                    <span className="font-mono">{p.not_applicable_kind_after}</span>
                  </>
                )}
              </>
            ) : p.rows_after_error !== null
              ? t("access.scopes.detail.impactUnknown", {
                  reason: p.rows_after_error,
                })
              : t("access.scopes.detail.impactRows", {
                  before:
                    p.rows_before === null
                      ? t("access.common.notComputed")
                      : String(p.rows_before),
                  after:
                    p.rows_after === null
                      ? t("access.common.notComputed")
                      : String(p.rows_after),
                })}
          </li>
        ))}
      </ul>
      <p className="text-[13px] text-neutral-600">{impact.effective}</p>
    </div>
  );
}
